import React, { useMemo, useState } from 'react';
import { HiOutlineEye } from 'react-icons/hi';
import SelectTextField from '../../../molecules/SelectTextField';
import { Template, ProviderTemplate } from '../../../../types/communication';
import { findInCatalog } from '../../../../utils/providerCatalog';
import { renderableContent } from '../composition/studio/studioDraft';
import { DraftPreviewFragment, useDraftPreview } from '../composition/studio/preview/useDraftPreview';
import EmailPreview from '../composition/studio/preview/EmailPreview';
import SlackPreview from '../composition/studio/preview/SlackPreview';
import TestValues from '../composition/studio/preview/TestValues';
import WhatsAppPreview from '../composition/studio/preview/WhatsAppPreview';
import { templateKind } from './templateKinds';

const ENVIRONMENTS = [
    { value: 'prd', label: 'Produção' },
    { value: 'hml', label: 'Homologação' },
    { value: 'dev', label: 'Desenvolvimento' },
    { value: 'sandbox', label: 'Sandbox' },
];

const BODY_FROM_COMPOSITION =
    '<p style="font-family:Inter,Arial,sans-serif;color:#6B7280;padding:32px;text-align:center">O corpo vem do modelo de corpo da composição.</p>';

const TemplatePreview: React.FC<{ template: Template; catalog?: ProviderTemplate[] }> = ({ template, catalog = [] }) => {
    const [env, setEnv] = useState('prd');
    const [testValues, setTestValues] = useState<Record<string, string>>({});

    const kind = templateKind(template);
    const envelope = template.envelope;

    const fragments = useMemo<DraftPreviewFragment[]>(() => {
        if (kind === 'EMAIL_BODY') return [{ key: 'body', content: renderableContent(template) }];
        if (kind === 'EMAIL_ENVELOPE' && envelope) {
            return [
                { key: 'subject', content: envelope.subject ?? '' },
                { key: 'preheader', content: envelope.preheader ?? '' },
                { key: 'from', content: envelope.from[0] ?? '' },
                { key: 'to', content: (envelope.to ?? []).join('\n') },
                ...(envelope.attachments ?? []).map((a, i) => ({ key: `att-${i}`, content: a.fileName || a.source })),
            ];
        }
        if (kind === 'INTERNAL_MESSAGE') return [{ key: 'message', content: template.content }];
        return [];
    }, [kind, template, envelope]);

    const { rendered } = useDraftPreview(fragments, testValues, env);

    const preview = () => {
        if (kind === 'EMAIL_BODY') {
            return <EmailPreview from="" to="" subject="(o assunto vem do modelo de envio)" preheader="" html={rendered.body ?? ''} attachments={[]} />;
        }
        if (kind === 'EMAIL_ENVELOPE' && envelope) {
            return (
                <EmailPreview
                    from={rendered.from ?? ''}
                    to={rendered.to ? rendered.to.split('\n').join(', ') : ''}
                    subject={rendered.subject ?? ''}
                    preheader={rendered.preheader ?? ''}
                    html={BODY_FROM_COMPOSITION}
                    attachments={(envelope.attachments ?? []).map((a, i) => ({
                        name: rendered[`att-${i}`] ?? a.fileName ?? a.source,
                        delivery: a.delivery,
                        expirationMinutes: a.expirationMinutes,
                    }))}
                />
            );
        }
        if (kind === 'INTERNAL_MESSAGE') return <SlackPreview channel="canal-da-composição" text={rendered.message ?? ''} />;
        const bindings = Object.fromEntries((template.providerBindings ?? []).map((b) => [b.placeholder, b.variable]));
        return <WhatsAppPreview provider={findInCatalog(catalog, template.providerTemplateName)} values={testValues} bindings={bindings} to="" />;
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-end gap-2">
                <HiOutlineEye className="text-gray-400" size={18} />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex-1">Pré-visualização</span>
                <SelectTextField
                    id="template-preview-environment"
                    label="Ambiente"
                    value={env}
                    hideEmptyOption
                    className="w-40"
                    items={ENVIRONMENTS}
                    onChange={(e) => setEnv(e.target.value)}
                />
            </div>
            {preview()}
            <TestValues
                variables={template.variables}
                values={testValues}
                onChange={(name, value) => setTestValues({ ...testValues, [name]: value })}
                onReset={() => setTestValues({})}
            />
        </div>
    );
};

export default TemplatePreview;
